import { Link } from "react-router-dom";
import {
  convertDateInReadableFormat,
  getPercentTagColor,
  truncate,
} from "../../Helper/Methods";
import { Tag } from "antd";

export const segregatedSitesReportColumns = [
  {
    title: "Site Number",
    dataIndex: "siteNumber",
    key: "siteNumber",
    width: 280, 
  },
  {
    title: "Safety Document ID",
    dataIndex: "safetyDatabaseReportNumber",
    key: "safetyDatabaseReportNumber",
    width: 280,
    render: (safetyDatabaseReportNumber: string, data: any) => {
      return (
        <Link to={`/reports/view/${data?.reportID}`}>
          {safetyDatabaseReportNumber}
        </Link>
      );
    },
  },

  {
    title: "Site Name",
    dataIndex: "siteName",
    key: "siteName",
    width: 280,
    render: (siteName: any) => {
      siteName = !siteName ? "-" : siteName;
      return siteName?.length === 60 ? truncate(siteName, 60) : siteName;
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    width: 280,
  },
  {
    title: "Study Number",
    dataIndex: "studyNumber",
    key: "studyNumber",
    width: 280,
  },
  {
    title: "Safety Document Type",
    dataIndex: "type",
    key: "type",
    width: 280,
    render: (type: any, record: any) => {
      return type === 0 ? record.typeValue : `${type}-day SUSAR`
    },
  },
  {
    title: "Day 0",
    dataIndex: "lrd",
    key: "lrd",
    width: 280,
    render: (lrd: string) => {
      return lrd ? convertDateInReadableFormat(lrd) : "-";
    },
  },
  {
    title: "Date of Notification (CD)",
    // Keyed on the scalar date sibling so sort/filter work through ReTable/antd;
    // render still reads the detailed array (date + calendar days) from the row.
    dataIndex: "sentOnDate",
    key: "sentOnDate",
    width: 280,
    render: (_sentOnDate: any, data: any) => {
      const sentOn = data?.sentOn;
      return (
        <div>
          {sentOn?.length > 0
            ? sentOn?.map((ele: any) => {
                const { date, calendarDays } = ele;
                return (
                  <p>{`${date ? convertDateInReadableFormat(date) : ""} ${
                    calendarDays ? `(${calendarDays})` : "-"
                  }`}</p>
                );
              })
            : "-"}
        </div>
      );
    },
  },
  {
    title: "Date of Acknowledgement (CD)",
    dataIndex: "acknowledgedOnDate",
    key: "acknowledgedOnDate",
    width: 280,
    render: (_acknowledgedOnDate: any, data: any) => {
      const acknowledgedOn = data?.acknowledgedOn;
      return (
        <div>
          {acknowledgedOn?.length > 0
            ? acknowledgedOn?.map((ele: any) => {
                const { date, calendarDays } = ele;
                return (
                  <p>{`${date ? convertDateInReadableFormat(date) : ""} ${
                    calendarDays ? `(${calendarDays})` : "-"
                  }`}</p>
                );
              })
            : "-"}
        </div>
      );
    },
  },
  {
    title: "Date of EC/IRB Notification (CD)",
    dataIndex: "irbNotifiedOnDate",
    key: "irbNotifiedOnDate",
    width: 280,
    render: (_irbNotifiedOnDate: any, data: any) => {
      const irbNotifiedOn = data?.irbNotifiedOn;
      return (
        <div>
          {irbNotifiedOn?.length > 0
            ? irbNotifiedOn?.map((ele: any) => {
                const { date, calendarDays } = ele;
                return (
                  <p>{`${date ? convertDateInReadableFormat(date) : ""} ${
                    calendarDays ? `(${calendarDays})` : "-"
                  }`}</p>
                );
              })
            : "-"}
        </div>
      );
    },
  },
  {
    title: "USDE ID",
    dataIndex: "reportID",
    key: "reportID",
    width: 280,
  },
];

export const aggregatedSitesReportColumns = [
  {
    title: "Site Number",
    dataIndex: "siteNumber",
    key: "siteNumber",
    width: 200,
    render: (data: string) => {
      return data || "-";
    },
  },
  {
    title: "Site Name",
    dataIndex: "siteName",
    key: "siteName",
    width: 200,
    render: (data: string) => {
      return data || "-";
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    width: 200,
    render: (data: string) => {
      return data || "-";
    },
  },
  {
    title: "Contact Country",
    dataIndex: "country",
    key: "country",
    width: 200,
    render: (data: string) => {
      return data || "-";
    },
  },
  {
    title: "Number of Safety Documents Shared",
    dataIndex: "totalDocShared",
    key: "totalDocShared",
    width: 280,
    render: (data: string) => {
      return data || "-";
    },
  },
  {
    title: "Site Notification Compliance",
    dataIndex: "siteNotificationCompliance",
    key: "siteNotificationCompliance",
    width: 280,
    render: (data: any) => {
      const tagColor = getPercentTagColor(data?.value);
      const percentVal = `${parseFloat(data?.value).toFixed(0)}%`;
      const value = `${String(data?.data)}/${String(data?.total)}`;
      return (
        <>
          <Tag color={tagColor} className="percentTag">
            {percentVal}
          </Tag>
          <span>{value}</span>
        </>
      );
    },
  },
  {
    title: "Site Acknowledgement Compliance",
    dataIndex: "siteAcknowledgementCompliance",
    key: "siteAcknowledgementCompliance",
    width: 280,
    render: (data: any) => {
      const tagColor = getPercentTagColor(data?.value);
      const percentVal = `${parseFloat(data?.value).toFixed(0)}%`;
      const value = `${String(data?.data)}/${String(data?.total)}`;
      return (
        <>
          <Tag color={tagColor} className="percentTag">
            {percentVal}
          </Tag>
          <span>{value}</span>
        </>
      );
    },
  },
  {
    title: "EC/IRB Notification Compliance",
    dataIndex: "ECIRBNotificationCompliance",
    key: "ECIRBNotificationCompliance",
    width: 280,
    render: (data: any) => {
      const tagColor = getPercentTagColor(data?.value);
      const percentVal = `${parseFloat(data?.value).toFixed(0)}%`;
      const value = `${String(data?.data)}/${String(data?.total)}`;
      return (
        <>
          <Tag color={tagColor} className="percentTag">
            {percentVal}
          </Tag>
          <span>{value}</span>
        </>
      );
    },
  },
];
